import React from 'react';
import styled from 'styled-components';
import { Body1, Body5, Container, Page } from '@ui';
import { useNavigate } from 'react-router';
import { ROUTES } from '@constants/routes';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import { handle400Statuses, sendApiRequest } from '@services/api';

import { AdvancedSettingsButton } from '@containers/DigitalAnalyst/AdvancedSettings';
import { AGENT_NAMES, AI_TOOLS } from '@constants';
import { Header } from '@app/navigation/header';
import { Body } from '@ui';
import { ChatWindow } from '@features/tableBuilder/ChatWindow';
import { useSelector, useDispatch } from 'react-redux';
import { Working } from '@ui/Working';
import { Table } from '@features/tableBuilder/Table';
import { logNetRequest } from '@utils/usefulFuncs';
import { tableBuilderActions } from '@constants/actionTypes';

const PageWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  h3 {
    text-transform: none;
  }
`;
const WorkspaceWrapper = styled.div`
  position: relative;
`;
const AgentSelectWrapper = styled.div`
  width: 244px;

  select {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 12px 16px;
    -webkit-appearance: auto !important;
  }
`;

const tool = AI_TOOLS.TABLE_BUILDER_TOOL;

const forceAgent = 'super_fast';
const disableAgent = false;

function TableBuilderTool() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [agentName, setAgentName] = React.useState(null);
  const [agents, setAgents] = React.useState([]);
  const { currentTableJSON, workingState, metadata } = useSelector((state) => state.tableBuilder);

  const handleCellChange = (rowIndex, columnIndex, value) => {
    dispatch({
      type: tableBuilderActions.updateCell,
      payload: { rowIndex, columnIndex, value },
    });

    // Check if we're editing a cell in the last row and the value is not empty
    if (value && value.trim() !== '') {
      const maxRow = currentTableJSON.length > 0 ? Math.max(...currentTableJSON.map((c) => c.rowIndex)) : -1;

      // If we're editing the last row, add a new empty row
      if (rowIndex === maxRow) {
        const maxCol = currentTableJSON.length > 0 ? Math.max(...currentTableJSON.map((c) => c.columnIndex)) + 1 : 1;
        const newRowIndex = maxRow + 1;

        // Create empty cells for all columns in the new row (only if they don't exist)
        const newRowCells = [];
        for (let colIndex = 0; colIndex < maxCol; colIndex++) {
          const cellExists = currentTableJSON.some(
            (cell) => cell.rowIndex === newRowIndex && cell.columnIndex === colIndex
          );

          if (!cellExists) {
            newRowCells.push({
              rowIndex: newRowIndex,
              columnIndex: colIndex,
              value: '',
            });
          }
        }

        // Dispatch actions to add the new row cells
        if (newRowCells.length > 0) {
          newRowCells.forEach((cell) => {
            dispatch({
              type: tableBuilderActions.addRow,
              payload: cell,
            });
          });
        }
      }
    }
  };

  const handleColumnWidthsCalculated = (measuredWidths) => {
    // Dispatch each column width to the store
    Object.entries(measuredWidths).forEach(([columnIndex, width]) => {
      dispatch({
        type: tableBuilderActions.setColumnWidth,
        payload: { columnIndex: parseInt(columnIndex), width },
      });
    });
  };

  const handleColumnResize = (columnIndex, width) => {
    dispatch({
      type: tableBuilderActions.setColumnWidth,
      payload: { columnIndex, width },
    });
  };

  const handleInsertColumn = (columnIndex) => {
    dispatch({
      type: tableBuilderActions.insertColumn,
      payload: { columnIndex },
    });
  };

  const handleDeleteRow = (rowIndex) => {
    dispatch({
      type: tableBuilderActions.deleteRowByIndex,
      payload: { rowIndex },
    });
  };

  const handleDeleteColumn = (columnIndex) => {
    dispatch({
      type: tableBuilderActions.deleteColumnByIndex,
      payload: { columnIndex },
    });
  };

  const handleInsertRow = (rowIndex) => {
    dispatch({
      type: tableBuilderActions.insertRowAt,
      payload: { rowIndex },
    });
  };

  const handleInsertColumnAt = (columnIndex) => {
    dispatch({
      type: tableBuilderActions.insertColumnAt,
      payload: { columnIndex },
    });
  };

  const onGoBack = () => navigate(ROUTES.home);

  const onAgentChange = (e) => {
    const {
      target: { value },
    } = e;
    setAgentName(value);
  };

  React.useEffect(() => {
    const getAgentName = async () => {
      const URL = `v1/genai_agent_script_definitions`;
      const { status, data } = await sendApiRequest('get', URL);
      logNetRequest(status, data);
      handle400Statuses(status);
      if (status === 200) {
        const agents = data.map((agent) => agent.script_name);
        setAgents(agents.sort());
        setAgentName(agents.includes(AGENT_NAMES.TORNADO_CHAT_ANALYST) ? AGENT_NAMES.TORNADO_CHAT_ANALYST : agents[0]);
      }
    };
    if (forceAgent) {
      setAgentName(forceAgent);
    } else {
      if (!disableAgent) {
        getAgentName();
      }
    }
  }, []);

  // Ensure there's always an empty row at the end when table first loads or state changes to complete
  React.useEffect(() => {
    if (workingState !== 'complete' && workingState !== 'errored') return;
    if (currentTableJSON.length === 0) return;

    const maxRow = Math.max(...currentTableJSON.map((c) => c.rowIndex));
    const maxCol = Math.max(...currentTableJSON.map((c) => c.columnIndex)) + 1;

    // Check if the last row has any non-empty values
    const lastRowCells = currentTableJSON.filter((cell) => cell.rowIndex === maxRow);
    const hasNonEmptyCell = lastRowCells.some((cell) => cell.value && cell.value.trim() !== '');

    // If the last row has content, add a new empty row
    if (hasNonEmptyCell) {
      const newRowIndex = maxRow + 1;
      const newRowCells = [];

      for (let colIndex = 0; colIndex < maxCol; colIndex++) {
        // Check if cell already exists (to avoid duplicates)
        const cellExists = currentTableJSON.some(
          (cell) => cell.rowIndex === newRowIndex && cell.columnIndex === colIndex
        );

        if (!cellExists) {
          newRowCells.push({
            rowIndex: newRowIndex,
            columnIndex: colIndex,
            value: '',
          });
        }
      }

      // Only dispatch if we have cells to add
      if (newRowCells.length > 0) {
        newRowCells.forEach((cell) => {
          dispatch({
            type: tableBuilderActions.addRow,
            payload: cell,
          });
        });
      }
    }
  }, [currentTableJSON, workingState, dispatch]);

  return (
    <PageWrapper>
      <Header />
      <Body>
        <Page width="1800px">
          <BackArrowButton onClick={onGoBack} />
          <AdvancedSettingsButton tool={tool} />
          <Container top={48}>
            <WorkspaceWrapper>
              <Container>
                <Body1 bold>Table Builder</Body1>
                <Container top={24}>
                  {forceAgent || disableAgent ? null : (
                    <Container top={16}>
                      <AgentSelectWrapper>
                        <select onChange={onAgentChange} value={agentName}>
                          {agents.map((agent) => (
                            <option key={agent} value={agent}>
                              {agent}
                            </option>
                          ))}
                        </select>
                      </AgentSelectWrapper>
                    </Container>
                  )}
                </Container>

                {workingState === 'unstarted' && (
                  <Container top={24}>
                    <Body5>Start by describing the table or chart you want to create.</Body5>
                  </Container>
                )}
                {workingState === 'working' && (
                  <Container top={24}>
                    <Working noVerticalPadding />
                  </Container>
                )}
                {(workingState === 'complete' || workingState === 'errored') && (
                  <Container top={8}>
                    <Table
                      data={currentTableJSON}
                      editable={true}
                      onCellChange={handleCellChange}
                      onColumnWidthsCalculated={handleColumnWidthsCalculated}
                      onColumnResize={handleColumnResize}
                      onInsertColumn={handleInsertColumn}
                      onDeleteRow={handleDeleteRow}
                      onDeleteColumn={handleDeleteColumn}
                      onInsertRow={handleInsertRow}
                      onInsertColumnAt={handleInsertColumnAt}
                      storedColumnWidths={metadata?.columnWidths || {}}
                    />
                  </Container>
                )}
              </Container>
              <ChatWindow agentName={agentName} />
            </WorkspaceWrapper>
            <Container top={122} />
          </Container>
        </Page>
      </Body>
    </PageWrapper>
  );
}

export default TableBuilderTool;
